import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, combineLatest, map, startWith } from 'rxjs';
import { ContribLegge5992WarningsService } from './warnings/contrib-legge-59-92.warnings';
import { contribLegge5992FormValidators } from './validators/contrib-legge-59-92.form.validators';
import { CoopValidators } from '@core/helpers/validators.helper';
import { ContribLegge5992FormListeners, manageErrorMessageLegge59 } from './listeners/contrib-legge-59-92.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';
import { getControl } from '@shared/helpers/form.helper';
import { DbCentraleEntity } from '../../models/interfaces/dati-business.interface';
import { Revisione53DatiBusinessSelectors } from '../../store/selectors/dati-business.selectors';

@Injectable()
export class ContribLegge5992FormService extends BaseRevisioneFormService<ContribLegge5992WarningsService> {
    constructor(protected warningsService: ContribLegge5992WarningsService) {
        super(warningsService);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Stato pratica
            'my_IdAssegnazione',
            'my_continuitaNelleRevisioni',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,
            `${formPaths.datiIdentificativi}.my_SoggDispostoRev`,
            `${formPaths.datiIdentificativi}.my_DataUltRev`,
            `${formPaths.datiIdentificativi}.my_data_immatricolazione_cci`,

            // Bilancio
            `${formPaths.bilancio}.my_AnnoPrimoEs`,
            `${formPaths.bilancio}.my_AnnoSecondoEs`,
            `${formPaths.bilancio}.my_BilancioAllegato`,
            `${formPaths.bilancio}.my_CkApplicatiRistorni`,
            `${formPaths.bilancio}.my_CkApplicaRistorniAnno1`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_DataApprovazione1`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil1`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil2`,
            `${formPaths.datiBilancio}.my_DataApprovazione1_60`,
            `${formPaths.datiBilancio}.my_DataApprovazione2_60`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcF231`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcF261`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcF232`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcF262`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAII1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAIII1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAIV1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAV1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVI1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVIItot1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVIII1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVII_new1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAX1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAII2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAIII2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAIV2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAV2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVI2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVIItot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVIII2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAVII_new2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAX2`,

            // Conclusioni
            `${formPaths.conclusioni}.my_DataCompilaz`,
        ]);
    }

    get centraleSoggDispostoRev$(): Observable<DbCentraleEntity | undefined> {
        const soggDispostoRevControl = getControl(this._form, `${formPaths.datiIdentificativi}.my_SoggDispostoRev`);
        return combineLatest([
            this.store.select(Revisione53DatiBusinessSelectors.getCentrale),
            soggDispostoRevControl.valueChanges.pipe(startWith(soggDispostoRevControl.value)),
        ]).pipe(map(([centrale, soggDispostoRev]) => centrale.find((c) => c.Id_cluster === soggDispostoRev)));
    }

    checkImportoComplessivo(): void {
        manageErrorMessageLegge59(this._form, this.toastrService);
    }

    override initListener(): void {
        new ContribLegge5992FormListeners(this._form, this.store, this.toastrService).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_IdAssegnazione: '',
                my_continuitaNelleRevisioni: '',
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                        my_SoggDispostoRev: '',
                        my_DataUltRev: '',
                        my_data_immatricolazione_cci: [{ value: '', disabled: true }],
                    }),
                    my_Bilancio: this.fb.group({
                        my_AnnoPrimoEs: '',
                        my_AnnoSecondoEs: '',
                        my_BilancioAllegato: '',
                        my_CkApplicatiRistorni: '',
                        my_CkApplicaRistorniAnno1: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno2: '',
                        my_BilAnno1: '',
                        my_DataApprovazione1: '',
                        my_TipoSchemaBil1: '',
                        my_TipoSchemaBil2: '',
                        my_DataApprovazione1_60: '',
                        my_DataApprovazione2_60: '',
                        my_ContoEconomico: this.fb.group({
                            my_CoEcF231: '',
                            my_CoEcF261: '',
                            my_CoEcF232: '',
                            my_CoEcF262: '',
                        }),
                        my_StatoPatrimoniale__Passivo: this.fb.group({
                            my_StPaPsAII1: '',
                            my_StPaPsAIII1: '',
                            my_StPaPsAIV1: '',
                            my_StPaPsAV1: '',
                            my_StPaPsAVI1: '',
                            my_StPaPsAVIItot1: '',
                            my_StPaPsAVIII1: '',
                            my_StPaPsAVII_new1: '',
                            my_StPaPsAX1: '',
                            my_StPaPsAII2: '',
                            my_StPaPsAIII2: '',
                            my_StPaPsAIV2: '',
                            my_StPaPsAV2: '',
                            my_StPaPsAVI2: '',
                            my_StPaPsAVIItot2: '',
                            my_StPaPsAVIII2: '',
                            my_StPaPsAVII_new2: '',
                            my_StPaPsAX2: '',
                        }),
                    }),

                    my_Conclusioni: this.fb.group({
                        my_DataCompilaz: '',
                    }),

                    my_ContributoLegge59: this.fb.group({
                        my_gruppo7: this.fb.group({
                            my_CkIrregPregL59: '',
                        }),

                        my_RisultEsercizio: ['', CoopValidators.required],
                        my_RisultEsercizioAnno1: ['', CoopValidators.required],
                        my_Preaccantonamenti: '',
                        my_PreaccantonamentiAnno1: '',
                        my_PreaccantonamentiInputatiIn: '',
                        my_PreaccantonamentiInputatiInAnno1: '',
                        my_Accantonamento: '',
                        my_AccantonamentoAnno1: '',
                        my_PerditeEserc: '',
                        my_PerditeEserc_calcolato: '',
                        my_PerditeEsercAnno1_calcolato: '',
                        my_PerditeEsercAnno1: '',
                        my_EventRistorni: '',
                        my_EventRistorniAnno1: '',
                        my_SommaAccantRistorni: ['', CoopValidators.required],
                        my_SommaAccantRistorniAnno1: ['', CoopValidators.required],
                        my_EventAccantUtiRisInd: '',
                        my_EventAccantUtiRisIndAnno1: '',
                        my_UtileAlNettoDiAccantUtiRisInd: '',
                        my_UtileAlNettoDiAccantUtiRisIndAnno1: '',
                        my_UtileAlNettoDiRisPerditePregr: '',
                        my_UtileAlNettoDiRisPerditePregrAnno1: '',
                        my_BaseCalcolo: ['', CoopValidators.required],
                        my_BaseCalcoloAnno1: ['', CoopValidators.required],
                        my_PercentualeVigente: '',
                        my_PercentualeVigenteAnno1: '',
                        my_ImportoComplessivo: '',
                        my_ImportoComplessivoAnno1: ['', CoopValidators.required],
                        my_ImportoVersato: '',
                        my_ImportoVersatoAnno1: '',
                        my_DataVersam: '',
                        my_DataVersamAnno1: '',
                        my_CkTipoFondoBen: '',
                        my_CkTipoFondoBenAnno1: '',
                        my_FondoBeneficiario: '',
                        my_FondoBeneficiarioAnno1: '',
                        my_CkVersamEffett: '',
                        my_DescVersamEffett: '',
                        my_RicevutaPagamento: '',
                        my_RicevutaPagamentoAnno1: '',
                        my_ChVersamFondMut: '',
                        my_ChVersamFondMutAnno1: '',
                        my_ObbligoAllegatoRicev3perc: '',
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: contribLegge5992FormValidators,
            }
        );
    }
}
